import { useState } from "react";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useGameUpdateHistory } from "@/api/game-api";
import type { GameUpdate } from "@/models/GameUpdate";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import { useSanitizedHtml } from "@/hooks/useSanitizedHtml";

/**
 * 게임 상세 「업데이트」 섹션 (#5472 · 시안 `29889:101460` 데스크탑 · `30035:19077` 모바일).
 *
 * ## ★두 폭이 같은 컴포넌트의 반응형이 아니다
 *
 * 시안 값이 축마다 갈린다 — 대칭으로 가정하면 여섯 군데가 틀린다:
 *
 * ```
 *              데스크탑 29889:101460        모바일 30035:19077
 * 제목          24/32 coolgray-100          22/28 coolgray-90
 * 접기          14/18 SemiBold cg-90        12/16 SemiBold cg-70
 * 행            py-16 · 보더 없음            py-14 · border-b coolgray-20
 * 행 배치        한 줄 (태그·제목·날짜)        두 줄 (태그+날짜 / 제목)
 * 태그          r-999 · px-12 py-4          r-4 · h-20 px-6
 * 버전 태그      bg yellow/12 · #6B5400      bg #FFF8E1 · yellow-60
 * 제목 글씨      16/22 SemiBold cg-80        14/18 Regular cg-90
 * 날짜          12/16 cg-60                 12/16 cg-50
 * ```
 *
 * `#FFF8E1`·`#6B5400` 두 색만 팔레트에 없어 hex 로 쓴다(나머지는 전부 토큰).
 * 데스크탑 버전 태그 배경도 시안이 `rgba(255,214,0,0.12)` 라 그대로 쓴다 —
 * `bg-bhaptics-yellow/12` 는 **CSS 가 생성되지 않아** 모바일 색이 그대로 남았다(실측).
 *
 * ## 데이터
 *
 * 응답이 **최신순**이라 「최신」 배지는 첫 항목에서 파생한다 — 서버에 `isLatest` 가 없다.
 * `changelog` 는 마크다운이고 **빈 문자열로 오는 항목이 있다**(dev `pubg-battlegrounds`
 * 의 `1.1.0`). 그 경우 펼침 자체를 막는다 — 열어도 빈 칸만 나온다.
 *
 * ⚠️ **이력이 없으면 섹션을 그리지 않는다.** prod 379개 중 이력 보유 **0건**(2026-09-01)
 * 이라 그게 기본 경로다. 빈 섹션 제목만 남기면 「업데이트가 끊긴 게임」으로 읽힌다.
 */
export default function GameUpdates({ slug }: { slug: string }) {
  const { t } = useTrans(["games"]);
  const { updates } = useGameUpdateHistory(slug);
  /**
   * ★**기본은 접힘이다** (#5692 · 기획: *"업데이트 기본 상태가 접혀진 상태고, 펼치기 버튼이
   * 있어야 합니다"*). 초판은 펼침이 기본이었다.
   */
  const [collapsed, setCollapsed] = useState(true);

  /**
   * ★**펼치면 각 버전 본문도 전부 열린다** (#5692: *"펼치면 모든 접혀있는 각각의 버전이
   * 열리면 됩니다"*).
   *
   * 그래서 행의 열림 상태를 **섹션이 소유한다** — 행이 자기 `useState` 를 들고 있으면
   * 섹션은 그걸 열 방법이 없다. 같은 불변식을 두 곳에서 지키면 서로의 뮤턴트를 가리므로
   * 소유자를 하나로 둔다.
   *
   * ⚠️ `펼치기` 를 누를 때마다 **다시 전부** 연다. 사용자가 몇 개를 손으로 닫아 둔 뒤
   * 접었다 펴도 기획대로 전부 열린다 — 「펼치면 전부」가 조건 없는 문장이다.
   *
   * ★**본문 없는 행을 걸러내지 않는다.** 초판은 `filter(본문 있음)` 을 넣었는데 그 방어가
   * **관측되지 않는다** — 행이 이미 `expandable` 로 본문·`aria-expanded`·쉐브론을 전부
   * 가리므로, 걸러도 안 걸러도 화면이 같다(실측: 그 필터를 지운 뮤턴트에 **11건 전부
   * 초록**). 같은 불변식을 두 곳에서 지키면 서로의 뮤턴트를 가리므로 **게이트는 행 하나만**
   * 둔다.
   */
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());

  /**
   * ★**`if (collapsed)` 는 남긴다 — 위 「관측 안 되면 걷는다」와 기준이 갈리는 이유**
   * (#5696 R1 N1).
   *
   * 이 조건도 **관측되지 않는다**(실측: 지운 뮤턴트에 11건 전부 초록). 접을 때 채워도
   * 목록이 언마운트되고 다음 `펼치기` 가 다시 전부 채우므로 최종 상태가 같다.
   *
   * 그런데 위 `filter(본문 있음)` 은 걷었고 이건 남긴다. **버린 이유가 다르기 때문이다:**
   *
   * ```
   *   filter(본문 있음)   같은 불변식을 지키는 게이트가 행에 **이미 있다** → 두 벌이라 걷는다
   *   if (collapsed)     중복이 아니라 **의도 표현**이다 — 채우는 것은 「펼칠 때」의 일이고,
   *                      조건 없이 쓰면 접을 때마다 무의미한 상태 쓰기가 남는다
   * ```
   *
   * 즉 기준은 「관측되나」 하나가 아니라 **「그 자리에 다른 소유자가 있나」**다.
   */
  const toggleSection = () => {
    if (collapsed) setOpenIds(new Set(updates.map((u) => u.id)));
    setCollapsed((v) => !v);
  };

  const toggleRow = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  if (updates.length === 0) return null;

  return (
    // 시안 — 섹션 내부 갭 20(두 폭 공통).
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl text-coolgray-90 laptop:text-3xl laptop:text-coolgray-100">
          {t("Updates")}
        </h2>
        <button
          type="button"
          onClick={toggleSection}
          aria-expanded={!collapsed}
          className="text-sm font-600 tracking-[0.4px] text-coolgray-70 laptop:text-base laptop:tracking-[0.25px] laptop:text-coolgray-90"
        >
          {collapsed ? t("Expand") : t("Collapse")}
        </button>
      </div>

      {!collapsed && (
        /**
         * ★**목록 높이를 8행으로 묶고 그 안에서 스크롤한다** (#5680).
         *
         * 기획(Notion 「패치노트 기획」 인라인 코멘트): *"패치가 많아질 경우 불필요한
         * 스크롤이 많아질 수 있어, 업데이트 탭의 최대 높이 고정, 해당 범위 안에서 스크롤
         * 이동 가능토록 지정. → 모든 업데이트를 **접기 상태로 보았을 때 8개 높이**."*
         *
         * ## 값의 유래 — `8 × 표준 행 높이`
         *
         * 행 높이는 `py` + 내용이고 두 폭이 다르다(모바일은 태그줄+제목 **2줄** 배치).
         * dev `fallout-4`(이력 11건) 실측 2026-09-09:
         *
         * ```
         *   1440   행 56px (11행 중 10)   → 8 × 56 = 448
         *    390   행 73px (11행 중 10)   → 8 × 73 = 584
         * ```
         *
         * ## ★★#5692 이후 — **재기 전에 행을 모두 접어라**
         *
         * `펼치기` 가 이제 **행 본문도 전부 연다**(#5692). 그래서 아래 명령을 펼친 직후
         * 그대로 돌리면 **열린 행**의 높이를 재서 값이 어긋난다. 실측(2026-09-10 로컬 dev
         * `fallout-4` 11건 · 1440):
         *
         * ```
         *   펼친 직후 그대로       최빈 130 → ×8 = 1040   ← 틀린 값 (2.3배)
         *   11개를 다 접고 나서    최빈  56 → ×8 =  448   ← 이 상수의 실제 근거
         * ```
         *
         * 위 「접기 상태로 보았을 때 8개」라는 기획 문구는 **여전히 이 값의 근거**지만,
         * #5692 이후 그 상태는 **사용자가 처음 보는 화면이 아니다** — 펼치면 열린 행이
         * 창을 채운다(1440 창 448 ↔ 내용 2040). 두 기획의 상호작용을 어떻게 정할지는
         * **#5698** 에서 확인 중이다. 답에 따라 이 상수와 아래 레시피의 전제가 바뀐다.
         *
         * ⚠️ **행의 `py` 를 바꾸면 이 값이 조용히 어긋난다.** 그때 다시 재라 —
         * 아래 명령이 폭별 최빈 행 높이를 인쇄한다(dev 서버 + `/browse`).
         * ★**돌리기 전에 열린 행을 모두 접는다**(위 실측 참조):
         *
         * ⚠️ **한 덩어리로 붙여넣으면 틀린 값이 나온다.** 접기는 React 상태 갱신이라
         * **같은 틱에 반영되지 않는다** — 클릭 직후 잰 높이는 아직 열린 행의 것이다
         * (실측: 한 덩어리로 돌리면 그대로 `1040`). 그래서 **두 번 나눠** 붙인다.
         *
         * ```js
         * // /en/games/experiences/fallout-4/ 에서 「펼치기」를 누른 뒤 ── ① 열린 행을 접는다
         * const list = [...document.querySelectorAll("h2")]
         *   .find(x => x.textContent.trim() === "Updates")
         *   .closest("div").parentElement.children[1];
         * [...list.children].forEach(r => {
         *   const b = r.querySelector("button");
         *   if (b && b.getAttribute("aria-expanded") === "true") b.click();
         * });
         * ```
         *
         * ```js
         * // ── ② 접힌 것을 눈으로 확인한 뒤 잰다
         * const list = [...document.querySelectorAll("h2")]
         *   .find(x => x.textContent.trim() === "Updates")
         *   .closest("div").parentElement.children[1];
         * const hs = [...list.children].map(r => Math.round(r.getBoundingClientRect().height));
         * const c = {}; hs.forEach(v => (c[v] = (c[v] || 0) + 1)); c;   // 최빈값 × 8
         * ```
         *
         * ## 수용한 것 — 제목이 두 줄인 행은 8번째에서 **잘려 보인다**
         *
         * 상한이 고정이라 긴 제목 행(1440 76px · 390 109px)이 앞 8개에 들어오면 그 행이
         * 중간에서 끊긴다(실측: 76px 중 56px 노출). 그건 **「아래 더 있다」는 어포던스**로
         * 읽히므로 그대로 둔다.
         *
         * 대안 둘을 검토하고 버렸다:
         *   · 행 경계까지 계산 — 온전한 행만 보이지만 **JS 로 행을 재야** 하고 컨테이너
         *     높이가 데이터에 따라 변한다. 「최대 높이 **고정**」이라는 문구와도 어긋난다.
         *   · 제목을 `line-clamp-1` 로 한 줄 고정 — 모든 행이 표준 높이가 되어 잘림이
         *     사라지지만, **표시가 바뀌는 디자인 결정**이라 여기서 내리지 않는다
         *     (기획의 「타이틀 ~20자 한 줄 요약」과는 맞는 방향이라 별건으로 남긴다).
         *
         * ## 헤더는 상한 **밖**이다
         *
         * 제목과 `접기` 버튼은 이 컨테이너 위에 있다 — 안에 넣으면 스크롤할 때 함께
         * 밀려 올라가 접기 컨트롤을 잃는다. 기획도 스크롤 대상을 「업데이트」 목록으로 적었다.
         *
         * ⚠️ 이력이 8개 이하면 내용이 상한보다 짧아 **아무 변화가 없다**(스크롤바도 안 뜬다).
         * prod 는 이력 보유 **0건**이라 지금은 어느 게임에서도 이 상한이 발동하지 않는다 —
         * dev `fallout-4` 가 유일한 확인 경로다.
         *
         * ★**출처는 상류 엔드포인트다** (#5685 R1 M1). 초판은 「앱이 쓰는 경로」로 적었는데
         * 그 문구가 앱의 API 라우트(`/api/games/{slug}/update-history/`)로 읽힌다 —
         * 그건 **prod 배포본에 없다**(실측 2026-09-09: prod **404 text/html** · dev 200
         * json). 그 경로로 prod 를 세면 HTML 을 받아 「0건」이 나온다.
         * 어디서든 재현되고 그게 정본인 상류로 적는다:
         *
         * ```bash
         * # 모집단(둘 다 실측 2026-09-09): 전체 379 · PC 풀 63
         * curl -s 'https://sdk-apis.bhaptics.com/api/v2/bhaptics-contents/by-filters?filters=pc,simracing&pageSize=500'
         * curl -s 'https://sdk-apis.bhaptics.com/api/v2/bhaptics-contents/<slug>/update-history'
         * ```
         *
         * ⚠️ 초판 라벨 「prod PC 379개」는 **틀렸다** — `platform=PC` 파라미터가 **무시돼서**
         * (같은 응답이 `pageSize=500` 단독과 379로 동일) 379 는 전체다. PC 풀은
         * `filters=pc,simracing` = **63**. 전수 결과는 두 모집단 다 **이력 보유 0 · 비정상 0**
         * 이라 결론은 그대로다.
         */
        <div className="flex max-h-[584px] flex-col overflow-y-auto laptop:max-h-[448px]">
          {updates.map((update, index) => (
            <UpdateRow
              key={update.id}
              update={update}
              isLatest={index === 0}
              open={openIds.has(update.id)}
              onToggle={() => toggleRow(update.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * `2026-07-28` — 시안 표기.
 *
 * ## ★「고정」은 **형식**이지 값이 아니다
 *
 * `toLocaleDateString` 을 안 쓰므로 **표기 형식**은 로케일과 무관하다. 다만
 * `getFullYear/getMonth/getDate` 는 **보는 사람의 타임존**으로 읽으므로 값 자체는
 * 사용자마다 다를 수 있다. 그게 맞는 동작인지가 `date` 필드의 성격에 달려 있어 재봤다.
 *
 * dev 이력 **전수 5건**(2026-09-02):
 *
 * ```
 * pubg-battlegrounds 1.2.0   2026-06-30 15:00:00Z   ← KST 자정
 * pubg-battlegrounds 1.1.0   2026-06-09 15:00:00Z   ← KST 자정
 * bhaptics-player    1.1.1   2026-07-29 03:52:48Z   ★자정 아님
 * bhaptics-player    1.0.0   2026-07-28 00:20:13Z   ★자정 아님
 * fallout-4          1.0.1   2026-07-31 02:40:22Z   ★자정 아님
 * ```
 *
 * **5건 중 3건이 임의 시각**이다 — 즉 이 필드는 「admin 이 고른 달력 날짜」가 아니라
 * **실제 시각(instant)** 이다. 시각이면 보는 사람의 타임존으로 그리는 게 맞고, 특정
 * 타임존(예: `Asia/Seoul`)으로 고정하면 나머지 3건이 오히려 틀어진다.
 *
 * ⚠️ 그래서 **KST 자정으로 저장된 2건은 KST 서쪽 사용자에게 하루 앞 날짜로 보인다.**
 * 그건 표시 버그가 아니라 **입력 쪽 모호함**이다 — 달력 날짜를 시각 필드에 담으면
 * 타임존이 붙는다. 근본 해법은 mgmt 가 날짜 문자열(`"2026-07-01"`)을 내려주는 것이고,
 * prod 에 이력이 생기면(현재 0건) 그때 다시 잴 값이다.
 */
const formatDate = (epochMillis: number) => {
  const d = new Date(epochMillis);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * ★열림 상태는 **프롭으로 받는다** — 자기 `useState` 를 들지 않는다 (#5692).
 * 섹션의 `펼치기` 가 모든 행을 여는데, 행이 상태를 소유하면 섹션이 열 방법이 없다.
 */
function UpdateRow({
  update,
  isLatest,
  open,
  onToggle,
}: {
  update: GameUpdate;
  isLatest: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTrans(["games"]);
  // 「본문이 있나」는 **원문**으로 판정한다 — HTML 은 빈 입력에도 껍데기 태그가 남을 수 있다.
  // ★이 게이트가 유일한 소유자다(섹션은 안 거른다 — 사유는 위 `openIds` 주석).
  const expandable = (update.changelog?.trim() ?? "").length > 0;
  const bodyHtml = useSanitizedHtml(update.changelogHtml ?? "");

  return (
    <div className="border-b border-coolgray-20 laptop:border-b-0">
      <button
        type="button"
        disabled={!expandable}
        onClick={onToggle}
        aria-expanded={expandable ? open : undefined}
        className={cn(
          "flex w-full items-center gap-1.5 py-3.5 text-left laptop:gap-5 laptop:py-4",
          !expandable && "cursor-default",
        )}
      >
        {/* 모바일은 두 줄(태그+날짜 / 제목), 데스크탑은 한 줄. */}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 laptop:flex-row laptop:items-center laptop:gap-5">
          <div className="flex items-center gap-1.5 laptop:gap-1">
            <span className="flex h-5 items-center justify-center rounded bg-[#FFF8E1] px-1.5 text-sm font-600 tracking-[0.4px] text-yellow-60 laptop:rounded-full laptop:bg-[rgba(255,214,0,0.12)] laptop:px-3 laptop:py-1 laptop:text-[#6B5400]">
              {update.versionName}
            </span>
            {isLatest && (
              <span className="flex h-5 items-center justify-center rounded bg-bhaptics-yellow px-1.5 text-sm font-600 tracking-[0.4px] text-coolgray-100 laptop:rounded-full laptop:px-3 laptop:py-1">
                {t("Latest")}
              </span>
            )}
            {/* 모바일은 날짜가 태그 줄에, 데스크탑은 제목 뒤에 온다. */}
            <span className="min-w-0 flex-1 text-sm tracking-[0.4px] text-coolgray-50 laptop:hidden">
              {formatDate(update.date)}
            </span>
          </div>

          <span className="w-full text-base tracking-[0.25px] text-coolgray-90 laptop:w-auto laptop:text-lg laptop:font-600 laptop:tracking-[0.5px] laptop:text-coolgray-80">
            {update.title}
          </span>

          <span className="hidden text-sm tracking-[0.4px] text-coolgray-60 laptop:inline">
            {formatDate(update.date)}
          </span>
        </div>

        {expandable && (
          <ChevronDownIcon
            className={cn(
              "h-6 w-6 shrink-0 transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        )}
      </button>

      {expandable && open && (
        /**
         * 펼침 본문 — 시안 `29889:101838`(데스크탑) · `30035:21811`(모바일).
         * **두 폭의 값이 같다**(둘 다 떠서 확인했다):
         *
         * ```
         * 컨테이너  flex-col gap-[20px] py-[28px]
         * 소제목    16/22 SemiBold #343A3F(cg-80) tracking .5
         * 항목      14/18 Regular  #697077(cg-60) tracking .25 · 항목 간 13
         * 소제목 → 그 아래 내용   7
         * ```
         *
         * ★마크다운 출력은 `h2 · ul · h2 · p` 처럼 **평평한 나열**이라 컨테이너 갭 하나로는
         * 「그룹 내 7 / 그룹 간 20」을 못 만든다. 그래서 갭을 **7** 로 두고 소제목에만
         * `mt-[13px]`(7+13=20)을 줘서 두 값을 동시에 만든다. 첫 소제목은 `mt-0`.
         *
         * ⚠️ 시안 본문은 불릿 없는 문단인데 실제 데이터는 마크다운 목록(`* 항목`)이다.
         * 데이터를 있는 그대로 그린다 — 불릿을 지우면 목록 구조가 사라진다.
         */
        <div
          className={cn(
            "flex flex-col gap-[7px] py-7",
            "[&>*]:m-0",
            "[&_h1]:mt-[13px] [&_h2]:mt-[13px] [&_h3]:mt-[13px]",
            "[&>:first-child]:mt-0",
            "[&_h1]:text-lg [&_h2]:text-lg [&_h3]:text-lg",
            "[&_h1]:font-600 [&_h2]:font-600 [&_h3]:font-600",
            "[&_h1]:tracking-[0.5px] [&_h2]:tracking-[0.5px] [&_h3]:tracking-[0.5px]",
            "[&_h1]:text-coolgray-80 [&_h2]:text-coolgray-80 [&_h3]:text-coolgray-80",
            "[&_p]:text-base [&_p]:tracking-[0.25px] [&_p]:text-coolgray-60",
            "[&_ul]:flex [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-[13px] [&_ul]:pl-5",
            "[&_ol]:flex [&_ol]:list-decimal [&_ol]:flex-col [&_ol]:gap-[13px] [&_ol]:pl-5",
            "[&_li]:text-base [&_li]:tracking-[0.25px] [&_li]:text-coolgray-60",
            "[&_a]:text-blue-60 [&_a]:underline",
          )}
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      )}
    </div>
  );
}
